import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  deployPublicationRelease,
  fetchPublicationRelease,
  isAmbiguousPublicationDeployFailure,
  listPublicationReleases,
  listPublicationTargetProfiles,
  preparePublicationCandidate,
  refreshPublicationReadback,
  requestPublicationAuthorization,
  requestPublicationRollbackCredential,
  requestPublicationStagingGrant,
  requestPublicationTakeDownCredential,
  rollbackPublicationRelease,
  runPublicationVerification,
  takeDownPublicationRelease,
} from "./publication-release-api";
import { TARGET_PROFILE_CATALOG_UNAVAILABLE } from "./publication-release-error";
import { useStudioStore } from "@/store/studio-store";
import {
  assertNoClientAuthoredPublicationOntologyRefs,
  type PublicationCandidateRequest,
  type PublicationDeployRequest,
  type PublicationReleaseAction,
  type PublicationReleaseProjection,
  type PublicationTargetProfileProjection,
} from "./publication-release-types";
import {
  getPublicationExecutionCredentialSnapshot,
  listPublicationExecutionCredentialEnvironments,
  readPublicationExecutionCredential,
  reconcilePublicationExecutionCredentials,
  reconcilePublicationExecutionPrincipal,
  removePublicationExecutionCredential,
  resolvePublicationExecutionAuthorityScope,
  storePublicationExecutionCredential,
  subscribePublicationExecutionCredentials,
  type PublicationExecutionCredentialBinding,
} from "./publication-execution-vault";

type PublicationLoadStatus = "idle" | "loading" | "ready" | "error";

const ACTIVE_RELEASE_STATES = new Set([
  "compiling",
  "verification_running",
  "authorization_pending",
  "deploying",
  "deployment_readback_pending",
  "live_readback_pending",
]);

export interface PublicationReleaseController {
  status: PublicationLoadStatus;
  release: PublicationReleaseProjection | null;
  releases: PublicationReleaseProjection[];
  targetProfiles: PublicationTargetProfileProjection[];
  selectedTargetProfileId: string | null;
  selectedTargetProfileVersion: string | null;
  error: string | null;
  pendingAction: PublicationReleaseAction | null;
  executionCredentialEnvironments: Array<"preview" | "staging" | "production">;
  selectTargetProfile: (profileId: string, profileVersion: string) => void;
  refresh: () => Promise<PublicationReleaseProjection | null>;
  /**
   * `applicationOntology` is deliberately NOT reachable through this input.
   * The hook holds no ontology authority: the refs it sends are the ones the
   * SERVER projected onto the release it already loaded, and a caller that
   * offers its own is refused rather than merged.
   */
  prepareCandidate: (
    input?: Partial<Omit<PublicationCandidateRequest, "applicationOntology">>,
  ) => Promise<PublicationReleaseProjection>;
  runVerification: () => Promise<PublicationReleaseProjection>;
  requestStagingGrant: () => Promise<PublicationReleaseProjection>;
  requestAuthorization: () => Promise<PublicationReleaseProjection>;
  deploy: (
    input: PublicationDeployRequest,
  ) => Promise<PublicationReleaseProjection>;
  refreshReadback: () => Promise<PublicationReleaseProjection>;
  rollback: (
    targetReceiptHash: string,
  ) => Promise<PublicationReleaseProjection>;
  takeDown: (
    targetReceiptHash: string,
  ) => Promise<PublicationReleaseProjection>;
}

export function usePublicationRelease(
  projectId: string | null | undefined,
): PublicationReleaseController {
  const [status, setStatus] = useState<PublicationLoadStatus>("idle");
  const [release, setRelease] = useState<PublicationReleaseProjection | null>(
    null,
  );
  const [releases, setReleases] = useState<PublicationReleaseProjection[]>([]);
  const [targetProfiles, setTargetProfiles] = useState<
    PublicationTargetProfileProjection[]
  >([]);
  const [selectedTargetProfileId, setSelectedTargetProfileId] = useState<
    string | null
  >(null);
  const [selectedTargetProfileVersion, setSelectedTargetProfileVersion] =
    useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] =
    useState<PublicationReleaseAction | null>(null);
  const principalId = useStudioStore((state) => state.user?.id ?? null);
  const credentialRevision = useSyncExternalStore(
    subscribePublicationExecutionCredentials,
    getPublicationExecutionCredentialSnapshot,
    getPublicationExecutionCredentialSnapshot,
  );
  const requestGeneration = useRef(0);
  const readAbortController = useRef<AbortController | null>(null);
  const executionAuthorityScopes = useMemo(
    () =>
      (["preview", "staging", "production"] as const).flatMap((environment) => {
        const scope = resolvePublicationExecutionAuthorityScope(
          principalId,
          release,
          environment,
        );
        return scope ? [scope] : [];
      }),
    [principalId, release],
  );

  const invalidateReadRequests = useCallback(() => {
    requestGeneration.current += 1;
    readAbortController.current?.abort();
    readAbortController.current = null;
  }, []);

  const beginReadRequest = useCallback(() => {
    invalidateReadRequests();
    const controller = new AbortController();
    readAbortController.current = controller;
    return {
      controller,
      generation: requestGeneration.current,
    };
  }, [invalidateReadRequests]);

  const cancelReadRequest = useCallback((controller: AbortController) => {
    controller.abort();
    if (readAbortController.current === controller) {
      readAbortController.current = null;
      requestGeneration.current += 1;
    }
  }, []);

  const loadLatest =
    useCallback(async (): Promise<PublicationReleaseProjection | null> => {
      if (!projectId) {
        invalidateReadRequests();
        setStatus("idle");
        setRelease(null);
        setReleases([]);
        setTargetProfiles([]);
        setSelectedTargetProfileId(null);
        setSelectedTargetProfileVersion(null);
        setError(null);
        return null;
      }
      const { controller, generation } = beginReadRequest();
      setStatus("loading");
      setError(null);
      try {
        const nextReleases = await listPublicationReleases(
          projectId,
          controller.signal,
        );
        let nextTargetProfiles: PublicationTargetProfileProjection[] = [];
        let targetProfileError: string | null = null;
        try {
          nextTargetProfiles = await listPublicationTargetProfiles(
            controller.signal,
          );
        } catch (cause) {
          targetProfileError = `${TARGET_PROFILE_CATALOG_UNAVAILABLE}: ${messageFrom(cause)}`;
        }
        if (
          controller.signal.aborted ||
          generation !== requestGeneration.current
        )
          return null;
        const sorted = [...nextReleases].sort((left, right) =>
          right.updatedAt.localeCompare(left.updatedAt),
        );
        setReleases(sorted);
        setRelease(sorted[0] ?? null);
        setTargetProfiles(nextTargetProfiles);
        const releaseTarget = sorted[0]?.targetProfile;
        const matchingReleaseProfile = releaseTarget
          ? nextTargetProfiles.find(
              (profile) =>
                profile.profileId === releaseTarget.profileId &&
                profile.profileVersion === releaseTarget.profileVersion,
            )
          : undefined;
        const defaultProfile = matchingReleaseProfile ?? nextTargetProfiles[0];
        setSelectedTargetProfileId(defaultProfile?.profileId ?? null);
        setSelectedTargetProfileVersion(defaultProfile?.profileVersion ?? null);
        setStatus("ready");
        setError(targetProfileError);
        return sorted[0] ?? null;
      } catch (cause) {
        if (
          controller.signal.aborted ||
          generation !== requestGeneration.current
        )
          return null;
        setStatus("error");
        setError(messageFrom(cause));
        return null;
      } finally {
        if (readAbortController.current === controller) {
          readAbortController.current = null;
        }
      }
    }, [beginReadRequest, invalidateReadRequests, projectId]);

  useEffect(() => {
    void loadLatest();
    return () => {
      invalidateReadRequests();
    };
  }, [invalidateReadRequests, loadLatest]);

  useEffect(() => {
    reconcilePublicationExecutionPrincipal(principalId);
  }, [principalId]);

  useEffect(() => {
    if (!projectId || !release) return;
    reconcilePublicationExecutionCredentials({
      principalId,
      projectId,
      releaseId: release.releaseId,
      validScopes: executionAuthorityScopes,
    });
  }, [executionAuthorityScopes, principalId, projectId, release]);

  useEffect(() => {
    if (
      !projectId ||
      !release ||
      status === "loading" ||
      pendingAction !== null ||
      !ACTIVE_RELEASE_STATES.has(release.derivedState)
    )
      return;
    let disposed = false;
    let timer: number | null = null;
    let pollController: AbortController | null = null;
    const schedule = () => {
      if (!disposed) timer = window.setTimeout(runPoll, 3_000);
    };
    const runPoll = () => {
      if (disposed) return;
      const { controller, generation } = beginReadRequest();
      pollController = controller;
      void fetchPublicationRelease(
        projectId,
        release.releaseId,
        controller.signal,
      )
        .then((next) => {
          if (
            controller.signal.aborted ||
            generation !== requestGeneration.current
          )
            return;
          setRelease(next);
          setReleases((current) => mergeRelease(current, next));
          setStatus("ready");
        })
        .catch((cause) => {
          if (
            controller.signal.aborted ||
            generation !== requestGeneration.current
          )
            return;
          setError(messageFrom(cause));
        })
        .finally(() => {
          if (readAbortController.current === controller) {
            readAbortController.current = null;
          }
          if (pollController === controller) pollController = null;
          schedule();
        });
    };
    schedule();
    return () => {
      disposed = true;
      if (timer !== null) window.clearTimeout(timer);
      if (pollController) cancelReadRequest(pollController);
    };
  }, [
    beginReadRequest,
    cancelReadRequest,
    pendingAction,
    projectId,
    release,
    status,
  ]);

  const applyAction = useCallback(
    async (
      action: PublicationReleaseAction,
      operation: () => Promise<PublicationReleaseProjection>,
    ): Promise<PublicationReleaseProjection> => {
      if (pendingAction)
        throw new Error("Another publication action is already running.");
      invalidateReadRequests();
      setPendingAction(action);
      setError(null);
      try {
        const next = await operation();
        setRelease(next);
        setReleases((current) => mergeRelease(current, next));
        setStatus("ready");
        return next;
      } catch (cause) {
        setError(messageFrom(cause));
        throw cause;
      } finally {
        setPendingAction(null);
      }
    },
    [invalidateReadRequests, pendingAction],
  );

  const requireContext = useCallback((): {
    projectId: string;
    release: PublicationReleaseProjection;
    releases: PublicationReleaseProjection[];
  } => {
    if (!projectId) throw new Error("Open a saved project first.");
    if (!release) throw new Error("Prepare a publication candidate first.");
    return { projectId, release, releases };
  }, [projectId, release, releases]);

  return useMemo(() => {
    // Re-read the shared in-memory vault whenever a credential is issued or consumed.
    void credentialRevision;
    return {
      status,
      release,
      releases,
      targetProfiles,
      selectedTargetProfileId,
      selectedTargetProfileVersion,
      error,
      pendingAction,
      executionCredentialEnvironments:
        projectId && release
          ? listPublicationExecutionCredentialEnvironments(
              executionAuthorityScopes,
            )
          : [],
      selectTargetProfile: (profileId: string, profileVersion: string) => {
        if (
          !targetProfiles.some(
            (profile) =>
              profile.profileId === profileId &&
              profile.profileVersion === profileVersion,
          )
        ) {
          setError(
            "The selected publication target profile is not registered.",
          );
          return;
        }
        setSelectedTargetProfileId(profileId);
        setSelectedTargetProfileVersion(profileVersion);
        setError(null);
      },
      refresh: loadLatest,
      prepareCandidate: (
        input: Partial<
          Omit<PublicationCandidateRequest, "applicationOntology">
        > = {},
      ) => {
        try {
          assertNoClientAuthoredPublicationOntologyRefs(input);
        } catch (cause) {
          return Promise.reject(cause);
        }
        if (!projectId)
          return Promise.reject(new Error("Open a saved project first."));
        const selectedProfile = targetProfiles.find(
          (profile) =>
            profile.profileId ===
              (input.targetProfileId ?? selectedTargetProfileId) &&
            profile.profileVersion ===
              (input.targetProfileVersion ?? selectedTargetProfileVersion),
        );
        if (!selectedProfile)
          return Promise.reject(
            new Error("Select a registered publication target profile first."),
          );
        return applyAction("prepare-candidate", () =>
          preparePublicationCandidate(projectId, {
            targetProfileId: selectedProfile.profileId,
            targetProfileVersion:
              input.targetProfileVersion ?? selectedProfile.profileVersion,
            ...(input.sourceRevisionHash
              ? { sourceRevisionHash: input.sourceRevisionHash }
              : {}),
            // Relayed from the server's own projection of the loaded release.
            // `undefined` when there is no loaded release yet, or when the
            // server projected none: the first candidate for a bound project
            // is the server's to bind, not the browser's to assert.
            applicationOntology: release?.applicationOntology,
          }),
        );
      },
      runVerification: () => {
        const context = requireContext();
        return applyAction("run-verification", () =>
          runPublicationVerification(
            context.projectId,
            context.release.releaseId,
          ),
        );
      },
      requestStagingGrant: () => {
        const context = requireContext();
        return applyAction("request-staging-grant", async () => {
          const adapterId = context.release.targetProfile.deploymentProfile;
          if (!adapterId)
            throw new Error(
              "The selected target profile has no registered deployment adapter.",
            );
          const idempotencyKey = createIdempotencyKey(
            "deploy-staging",
            context.release.releaseId,
          );
          const result = await requestPublicationStagingGrant(
            context.projectId,
            context.release.releaseId,
            adapterId,
            idempotencyKey,
            context.release.applicationOntology,
          );
          const scope = resolvePublicationExecutionAuthorityScope(
            principalId,
            result.release,
            "staging",
          );
          if (!scope)
            throw new Error(
              "The staging grant is missing an exact unexpired signed authority.",
            );
          storePublicationExecutionCredential(scope, {
            credential: result.executionCredential,
            idempotencyKey,
            adapterId,
          });
          return result.release;
        });
      },
      requestAuthorization: () => {
        const context = requireContext();
        return applyAction("request-authorization", async () => {
          const adapterId = context.release.targetProfile.deploymentProfile;
          if (!adapterId)
            throw new Error(
              "The selected target profile has no registered deployment adapter.",
            );
          const idempotencyKey = createIdempotencyKey(
            "deploy-production",
            context.release.releaseId,
          );
          const result = await requestPublicationAuthorization(
            context.projectId,
            context.release.releaseId,
            adapterId,
            idempotencyKey,
            context.release.applicationOntology,
          );
          const scope = resolvePublicationExecutionAuthorityScope(
            principalId,
            result.release,
            "production",
          );
          if (!scope)
            throw new Error(
              "The production authorization is missing an exact unexpired signed authority.",
            );
          storePublicationExecutionCredential(scope, {
            credential: result.executionCredential,
            idempotencyKey,
            adapterId,
          });
          return result.release;
        });
      },
      deploy: (input: PublicationDeployRequest) => {
        const context = requireContext();
        if (pendingAction)
          return Promise.reject(
            new Error("Another publication action is already running."),
          );
        let executionCredential: PublicationExecutionCredentialBinding;
        const scope = resolvePublicationExecutionAuthorityScope(
          principalId,
          context.release,
          input.environment,
        );
        if (!scope)
          return Promise.reject(
            new Error(
              `Request a new ${input.environment} execution credential before deploying.`,
            ),
          );
        try {
          executionCredential = readPublicationExecutionCredential(
            scope,
            input.adapterId,
          );
        } catch (cause) {
          return Promise.reject(cause);
        }
        return applyAction(
          input.environment === "production"
            ? "deploy-production"
            : "deploy-staging",
          async () => {
            try {
              const next = await deployPublicationRelease(
                context.projectId,
                context.release.releaseId,
                {
                  ...input,
                  idempotencyKey: executionCredential.idempotencyKey,
                  executionCredential: executionCredential.credential,
                  applicationOntology: context.release.applicationOntology,
                },
              );
              removePublicationExecutionCredential(
                scope,
                executionCredential.idempotencyKey,
              );
              return next;
            } catch (cause) {
              if (!isAmbiguousPublicationDeployFailure(cause)) {
                removePublicationExecutionCredential(
                  scope,
                  executionCredential.idempotencyKey,
                );
              }
              throw cause;
            }
          },
        );
      },
      refreshReadback: () => {
        const context = requireContext();
        return applyAction("refresh-readback", () =>
          refreshPublicationReadback(
            context.projectId,
            context.release.releaseId,
          ),
        );
      },
      rollback: (targetReceiptHash: string) => {
        const context = requireContext();
        return applyAction("rollback", async () => {
          const environment = context.release.deployment?.environment;
          if (
            environment !== "preview" &&
            environment !== "staging" &&
            environment !== "production"
          ) {
            throw new Error(
              "Rollback requires a deployed preview, staging, or production environment.",
            );
          }
          const target = context.release.rollbackTargets.find(
            (candidate) =>
              candidate.environment === environment &&
              candidate.receiptRef.contentHash === targetReceiptHash,
          );
          if (!target) {
            throw new Error(
              "Rollback requires an exact server-authorized same-environment deployment receipt.",
            );
          }
          const targetDeploymentReceiptRef = target.receiptRef;
          const idempotencyKey = createIdempotencyKey(
            "rollback",
            context.release.releaseId,
          );
          const confirmation = {
            confirmed: true as const,
            candidateId: context.release.candidateId,
            outputHash: target.outputHash,
            targetDeploymentReceiptRef,
          };
          const credential = await requestPublicationRollbackCredential(
            context.projectId,
            context.release.releaseId,
            environment,
            targetDeploymentReceiptRef,
            confirmation,
            idempotencyKey,
            context.release.applicationOntology,
          );
          return rollbackPublicationRelease(
            context.projectId,
            context.release.releaseId,
            {
              targetDeploymentReceiptRef,
              idempotencyKey,
              executionCredential: credential.executionCredential,
              confirmation,
              applicationOntology: context.release.applicationOntology,
            },
          );
        });
      },
      takeDown: (targetReceiptHash: string) => {
        const context = requireContext();
        return applyAction("take-down", async () => {
          const targetDeploymentReceiptRef =
            context.release.deployment?.receiptRef;
          const targetOutputHash = context.release.deployment?.outputHash;
          if (
            !targetDeploymentReceiptRef ||
            !targetOutputHash ||
            targetDeploymentReceiptRef.contentHash !== targetReceiptHash
          ) {
            throw new Error(
              "Take-down requires the exact current deployment receipt reference.",
            );
          }
          const idempotencyKey = createIdempotencyKey(
            "take-down",
            context.release.releaseId,
          );
          const environment = context.release.deployment?.environment;
          if (
            environment !== "preview" &&
            environment !== "staging" &&
            environment !== "production"
          ) {
            throw new Error(
              "Take-down requires an exact deployed preview, staging, or production environment.",
            );
          }
          const credential = await requestPublicationTakeDownCredential(
            context.projectId,
            context.release.releaseId,
            environment,
            targetDeploymentReceiptRef,
            idempotencyKey,
            context.release.applicationOntology,
          );
          return takeDownPublicationRelease(
            context.projectId,
            context.release.releaseId,
            {
              targetDeploymentReceiptRef,
              idempotencyKey,
              executionCredential: credential.executionCredential,
              confirmation: {
                confirmed: true,
                candidateId: context.release.candidateId,
                outputHash: targetOutputHash,
                targetDeploymentReceiptRef,
              },
              applicationOntology: context.release.applicationOntology,
            },
          );
        });
      },
    };
  }, [
    applyAction,
    error,
    credentialRevision,
    executionAuthorityScopes,
    loadLatest,
    pendingAction,
    principalId,
    projectId,
    release,
    releases,
    selectedTargetProfileId,
    selectedTargetProfileVersion,
    targetProfiles,
    requireContext,
    status,
  ]);
}

function mergeRelease(
  current: PublicationReleaseProjection[],
  next: PublicationReleaseProjection,
): PublicationReleaseProjection[] {
  return [
    next,
    ...current.filter((item) => item.releaseId !== next.releaseId),
  ].sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
}

function messageFrom(value: unknown): string {
  return value instanceof Error ? value.message : "Publication request failed.";
}

function createIdempotencyKey(operation: string, releaseId: string): string {
  const nonce =
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return `${operation}:${releaseId}:${nonce}`;
}
